import { useQuery } from '@tanstack/react-query';
import { supabase, tables } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ErrorCategory } from '@/utils/errorHandler';
import { useMemo } from 'react';

export interface AtRiskStudent {
  id: string;
  first_name: string;
  last_name: string;
  attendance_percentage: number;
  classes_attended: number;
  total_classes: number;
  last_attendance_date: string | null;
}

export interface AtRiskStudentsData {
  students: AtRiskStudent[];
  count: number;
}

/**
 * Hook to fetch students with attendance <60% in the last 4 weeks
 * Simplified version for initial testing
 */
export const useAtRiskStudents = () => {
  const { user } = useAuth();
  const errorHandler = useErrorHandler('useAtRiskStudents');
  
  const organizationId = useMemo(() => {
    return user?.user_metadata?.organization_id as string | undefined;
  }, [user?.user_metadata?.organization_id]);

  return useQuery({
    queryKey: ['atRiskStudents', organizationId] as const,
    queryFn: async (): Promise<AtRiskStudentsData> => {
      if (!organizationId) {
        throw errorHandler.handleError(
          new Error('No organization ID found'),
          'fetchAtRiskStudents',
          { userId: user?.id },
          false
        );
      }

      try {
        // Calculate date 4 weeks ago
        const fourWeeksAgo = new Date();
        fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
        const fourWeeksAgoStr = fourWeeksAgo.toISOString().split('T')[0];

        // Get all active students for the organization
        const { data: students, error: studentsError } = await tables
          .students()
          .select('id, first_name, last_name')
          .eq('organization_id', organizationId);

        if (studentsError) throw studentsError;

        if (!students || students.length === 0) {
          return { students: [], count: 0 };
        }

        // Get attendance records for the last 4 weeks
        const { data: attendanceRecords, error: attendanceError } = await tables
          .class_attendance()
          .select('student_id, date, status')
          .eq('organization_id', organizationId)
          .gte('date', fourWeeksAgoStr);

        if (attendanceError) throw attendanceError;

        // Calculate attendance for each student
        const atRiskStudents: AtRiskStudent[] = [];

        for (const student of students) {
          // Get student's attendance records
          const studentAttendance = attendanceRecords?.filter(
            record => record.student_id === student.id
          ) || [];

          // Count total classes and attended classes
          const totalClasses = studentAttendance.length;
          const classesAttended = studentAttendance.filter(
            record => record.status === 'present' || record.status === 'late'
          ).length;

          // Skip students with no attendance records
          if (totalClasses === 0) {
            continue;
          }

          const attendancePercentage = (classesAttended / totalClasses) * 100;

          // Find last attendance date
          const lastAttendance = studentAttendance
            .filter(record => record.status === 'present' || record.status === 'late')
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

          // Add to at-risk list if attendance < 60%
          if (attendancePercentage < 60) {
            atRiskStudents.push({
              id: student.id,
              first_name: student.first_name || '',
              last_name: student.last_name || '',
              attendance_percentage: Math.round(attendancePercentage),
              classes_attended: classesAttended,
              total_classes: totalClasses,
              last_attendance_date: lastAttendance?.date || null,
            });
          }
        }

        // Sort by attendance percentage (lowest first)
        atRiskStudents.sort((a, b) => a.attendance_percentage - b.attendance_percentage);

        return {
          students: atRiskStudents,
          count: atRiskStudents.length,
        };
      } catch (error) {
        throw errorHandler.handleError(
          error,
          'fetchAtRiskStudents',
          { 
            userId: user?.id,
            organizationId,
            category: ErrorCategory.DATABASE
          },
          false
        );
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep unused data in cache for 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error?.message?.includes('organization_id')) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid excessive queries
    enabled: !!organizationId,
  });
};